import { HttpHeaders } from "@angular/common/http";

/**
 * Method to use to look up file name from response headers
 *
 * @param headers
 * @returns
 */
export function getDownloadFileName(headers: HttpHeaders) {
  const contentDisposition = headers.get("Content-Disposition");
  if (contentDisposition?.includes("attachment")) {
    const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = fileNameRegex.exec(contentDisposition);
    if (matches?.[1]) {
      const fileName = matches[1].replace(/['"]/g, "");
      return fileName;
    }
  }

  return undefined;
}

/**
 * Method is used to download file from server.
 *
 * @param fileName
 * @param data
 */
export function downloadFile(
  fileName: string,
  data: Blob | ArrayBuffer | string,
  mimeType?: string,
) {
  const fileType = mimeType ?? getFileType(fileName);

  let url: string | undefined;
  if (typeof data !== "string" || !data.startsWith("data:")) {
    const blob = new Blob([data], { type: fileType });
    url = window.URL.createObjectURL(blob);
  } else {
    url = data;
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
}

/**
 * Return the blob content type field for each file extension type
 * @param extension -- the file extension
 */
export function getFileType(extension: string): string {
  const defaultFileType = "text/plain"; // e.g. extensionLower == '.txt'

  if (!extension) {
    return defaultFileType;
  }

  const extensionLower: string = extension.toLowerCase();

  const extensionMimePairs = [
    [["png"], "image/x-png"],
    [["gif"], "image/gif"],
    [["jpg", "jpeg", "jpe", "pfif", "pjpeg", "pjp"], "image/jpeg"],
    [
      ["xls", "xlsx", "xlsm"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
    ],
    [["csv"], "text/csv"],
    [["pdf"], "application/pdf"],
  ] satisfies [string[], string][];
  for (const [ext, mime] of extensionMimePairs) {
    for (const inner of ext) {
      if (extensionLower.endsWith(inner)) {
        return mime;
      }
    }
  }

  return defaultFileType;
}

/**
 * Return true if the input mime type is one that is an image
 * @param mimeType -- the input mime type
 */
export function isImageType(mimeType: string | undefined): boolean | undefined {
  return mimeType?.includes("image");
}

/**
 * Download a file from a URL
 * @param downloadUrl -- the download URL
 * @param fileName -- the file name
 */
export function downloadFileFromUrl(
  downloadUrl: string,
  fileName?: string,
): void {
  const link = document.createElement("a");
  link.href = downloadUrl;
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
